import React, { useState, useEffect } from 'react';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface OptionFormSliderProps {
  workspaceId: string;
  optionId?: string;
  projectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

interface Project {
  _id: string;
  name: string;
}

interface Category {
  _id: string;
  name: string;
}

interface PackageLocation {
  _id: string;
  name: string;
  order: number;
}

interface Phase {
  _id: string;
  name: string;
}

interface OptionFormData {
  order: number;
  projectId: string;
  name: string;
  caption: string;
  categoryId: string;
  location: string;
  phase: string;
  isActive: boolean;
  isValid: boolean;
  displayQuantity: boolean;
}

const OptionFormSlider: React.FC<OptionFormSliderProps> = ({
  workspaceId,
  optionId,
  projectId,
  onSuccess,
  onCancel,
  onLoadingChange
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<PackageLocation[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [nextOrder, setNextOrder] = useState<number>(0);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const initialFormData: OptionFormData = {
    order: nextOrder,
    projectId: projectId || '',
    name: '',
    caption: '',
    categoryId: '',
    location: '',
    phase: '',
    isActive: false,
    isValid: true,
    displayQuantity: true
  };

  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, [workspaceId]);

  useEffect(() => {
    if (projectId && !optionId) {
      fetchLocations(projectId);
      fetchNextOrder(projectId);
    }
  }, [projectId, optionId, workspaceId]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-categories`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchLocations = async (selectedProjectId: string) => {
    if (!selectedProjectId) {
      setLocations([]);
      return;
    }

    try {
      setLoadingLocations(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkglocations`, {
        params: {
          projectId: selectedProjectId,
          limit: 1000,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchNextOrder = async (selectedProjectId: string, updateCallback?: (order: number) => void) => {
    try {
      setIsLoadingOrder(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-options`, {
        params: { projectId: selectedProjectId, limit: 1, sortBy: 'order', sortOrder: 'desc' }
      });

      const options = response.data.options;
      const nextOrderValue = options && options.length > 0 ? options[0].order + 1 : 0;

      setNextOrder(nextOrderValue);
      if (updateCallback) {
        updateCallback(nextOrderValue);
      }
    } catch (error) {
      console.error('Error fetching next order:', error);
      setNextOrder(0);
      if (updateCallback) {
        updateCallback(0);
      }
    } finally {
      setIsLoadingOrder(false);
    }
  };

  // Static phase list - no need to fetch
  const phases = [
    { _id: 'phase-1', name: 'One' },
    { _id: 'phase-2', name: 'Two' },
    { _id: 'phase-3', name: 'Three' },
    { _id: 'phase-4', name: 'Four' },
    { _id: 'phase-5', name: 'Five' }
  ];

  const validateForm = (data: OptionFormData): string | null => {
    if (!data.projectId) {
      return 'Project is required';
    }
    if (!data.name.trim()) {
      return 'Name is required';
    }
    if (!data.location.trim()) {
      return 'Location is required';
    }
    if (!data.phase) {
      return 'Phase is required';
    }
    return null;
  };

  const transformFetchData = (response: any): OptionFormData => {
    const option = response.option;

    // Load locations when editing
    if (option.projectId) {
      fetchLocations(option.projectId);
    }

    return {
      order: option.order || 1,
      projectId: option.projectId || '',
      name: option.name || '',
      caption: option.caption || '',
      categoryId: option.categoryId || '',
      location: option.location || '',
      phase: option.phase || '',
      isActive: option.isActive ?? false,
      isValid: option.isValid ?? true,
      displayQuantity: option.displayQuantity ?? true
    };
  };

  const transformSubmitData = (data: OptionFormData) => {
    const submitData: any = {
      order: data.order,
      projectId: data.projectId,
      name: data.name.trim(),
      caption: data.caption.trim() || undefined,
      categoryId: data.categoryId || undefined,
      location: data.location.trim(),
      phase: data.phase,
      isActive: data.isActive,
      isValid: data.isValid,
      displayQuantity: data.displayQuantity
    };

    return submitData;
  };

  // Don't render the form until projects and order are loaded for new options
  if (!optionId && (loadingProjects || (projectId && isLoadingOrder))) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={`option-form-${nextOrder}`}
      workspaceId={workspaceId}
      itemId={optionId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/pkg-options`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="option-form"
      onLoadingChange={onLoadingChange}
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div className="form-group">
            <label htmlFor="order" className="form-label">
              Order
            </label>
            <input
              type="number"
              id="order"
              name="order"
              className="form-input"
              value={formData.order}
              onChange={handleInputChange}
              required
              disabled={true}
              min="0"
            />
            <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
              Auto-generated based on existing options
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading projects...</p>
              </div>
            ) : (
              <>
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (!optionId && e.target.value) {
                      fetchNextOrder(e.target.value, (order) => {
                        handleFieldChange('order', order);
                      });
                    }
                  }}
                  required
                  disabled={loading || !!optionId}
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {optionId && (
                  <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                    Project cannot be changed after creation
                  </small>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter option name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="caption" className="form-label">
              Caption
            </label>
            <input
              type="text"
              id="caption"
              name="caption"
              className="form-input"
              value={formData.caption}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="Enter caption"
            />
          </div>

          <div className="form-group">
            <label htmlFor="categoryId" className="form-label">
              Category
            </label>
            {loadingCategories ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading categories...</p>
              </div>
            ) : (
              <select
                id="categoryId"
                name="categoryId"
                className="form-input"
                value={formData.categoryId}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="location" className="form-label">
              Locations
            </label>
            {loadingLocations ? (
              <select className="form-input" disabled>
                <option>Loading locations...</option>
              </select>
            ) : (
              <select
                id="location"
                name="location"
                className="form-input"
                value={formData.location}
                onChange={handleInputChange}
                required
                disabled={loading || !formData.projectId}
              >
                <option value="">Select Location</option>
                {locations.map((location) => (
                  <option key={location._id} value={location.name}>
                    {location.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phase" className="form-label">
              Phase
            </label>
            <select
              id="phase"
              name="phase"
              className="form-input"
              value={formData.phase}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              <option value="">Select Phase</option>
              {phases.map((phase) => (
                <option key={phase._id} value={phase.name}>
                  {phase.name}
                </option>
              ))}
            </select>
          </div>

          <div className="drawer-form-checkbox-group" style={{ flexDirection: 'column', gap: '12px' }}>
            <label className="drawer-form-checkbox-label" htmlFor="isActive">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
                disabled={loading}
              />
              <span>Active</span>
            </label>

            <label className="drawer-form-checkbox-label" htmlFor="isValid">
              <input
                type="checkbox"
                id="isValid"
                name="isValid"
                checked={formData.isValid}
                onChange={(e) => handleFieldChange('isValid', e.target.checked)}
                disabled={loading}
              />
              <span>Valid</span>
            </label>

            <label className="drawer-form-checkbox-label" htmlFor="displayQuantity">
              <input
                type="checkbox"
                id="displayQuantity"
                name="displayQuantity"
                checked={formData.displayQuantity}
                onChange={(e) => handleFieldChange('displayQuantity', e.target.checked)}
                disabled={loading}
              />
              <span>Display Quantity</span>
            </label>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default OptionFormSlider;
